import { useState } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVerticalIcon, HistoryIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { useDeskId } from '@/hooks/useDeskId';
import { cn } from '@/lib/utils';
import { ReorderProps } from '@/modules/rules/hooks/useRules';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { Rule } from '@/shared/types/rules';
import { RootState } from '@/shared/types/state';
import { timeFromNow } from '@/util/dateUtils';

import { Actions } from './Actions';
import { OrderInput } from './OrderInput';
import { RuleTooltipContent } from './RuleTooltipContent';
import { Status } from './Status';

interface RuleTileProps {
  slug: string;
  rule: Rule;
  reorderRule: (params: ReorderProps) => void;
  reorderIsLoading: boolean;
  totalRules: number;
  isDragDisabled: boolean;
}

export const RuleTile = ({
  slug,
  rule,
  reorderRule,
  reorderIsLoading,
  totalRules,
  isDragDisabled,
}: RuleTileProps) => {
  const { rule_id, name, description, status, last_used_at } = rule;
  const { t } = useTranslation();
  const deskId = useDeskId();
  const { ruleId: selectedRuleId } = useParams();
  const isSelected = selectedRuleId === rule_id;
  const rulesPath = `/${slug}/environments/${deskId}/rules`;
  const isInactive = status === 'inactive';
  // Held shut over the last-used date and while the menu is open, so tooltips never stack.
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isOverLastUsed, setIsOverLastUsed] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isTooltipSuppressed = isMenuOpen || (isOverLastUsed && Boolean(last_used_at));

  const canWrite = useSelector((state: RootState) => getPermissions(state, 'rules', actions.WRITE));
  const currentPosition = rule.position;

  const isSortingDisabled = isDragDisabled || !canWrite || reorderIsLoading;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rule_id,
    disabled: isSortingDisabled,
  });

  const style = { transform: CSS.Translate.toString(transform), transition };

  const renderDragHandle = () => {
    if (isDragDisabled) {
      return (
        <InfoTooltip content={t('rules.drag_disabled_tooltip')} side="left">
          <GripVerticalIcon
            aria-hidden="true"
            className="mr-2 cursor-not-allowed text-foreground-muted opacity-50"
            size={16}
          />
        </InfoTooltip>
      );
    }

    return (
      <span
        role="button"
        aria-label={t('rules.drag_handle', { 0: name })}
        aria-roledescription={t('rules.sortable')}
        className={cn(
          'mr-2 cursor-grab rounded-sm ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none active:cursor-grabbing',
          reorderIsLoading && 'cursor-progress',
          !canWrite && 'cursor-not-allowed opacity-50'
        )}
        {...attributes}
        {...listeners}
      >
        <GripVerticalIcon aria-hidden="true" className="text-foreground-muted" size={16} />
      </span>
    );
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      aria-label={name}
      className={cn('flex w-full max-w-xl items-center', isDragging && 'opacity-50')}
    >
      {renderDragHandle()}
      <InfoTooltip
        content={
          <RuleTooltipContent name={name} description={description} actions={rule.actions} />
        }
        variant="rich"
        size="auto"
        side="right"
        align="start"
        delayDuration={300}
        open={isTooltipOpen && !isTooltipSuppressed}
        onOpenChange={setIsTooltipOpen}
        className="w-59 overflow-hidden"
      >
        <Link
          className={cn(
            'relative flex h-auto flex-1 items-center gap-4 overflow-hidden rounded-lg border border-stroke bg-background px-4 py-4 text-sm leading-5 font-normal transition-colors hover:bg-highlight-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            isSelected && 'border-informational bg-bg-info-soft hover:bg-bg-info-soft'
          )}
          aria-current={isSelected ? 'page' : undefined}
          draggable="false"
          to={`${rulesPath}/${rule_id}`}
          // Close on leave ourselves: Radix closes from the content, which is not mounted while held shut.
          onPointerLeave={() => setIsTooltipOpen(false)}
          // Only focus on the tile itself opens the tooltip, not focus on the order input or the menu.
          onFocus={(e) => {
            if (e.target !== e.currentTarget) {
              e.preventDefault();
            }
          }}
        >
          <OrderInput
            key={currentPosition}
            currentPosition={currentPosition}
            totalRules={totalRules}
            ruleId={rule_id}
            disabled={!canWrite || reorderIsLoading}
            onReorder={reorderRule}
          />

          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 pr-8">
            <div className="flex min-w-0 items-center gap-3">
              <span
                className={cn(
                  'min-w-0 truncate text-base leading-none font-medium text-foreground',
                  isInactive && 'text-foreground-muted opacity-65'
                )}
              >
                {name}
              </span>
              <Status status={status} />
            </div>

            {description && (
              <span
                className={cn(
                  'max-w-105 truncate text-sm leading-5 font-normal text-foreground-muted',
                  isInactive && 'opacity-65'
                )}
              >
                {description}
              </span>
            )}

            <span
              className="flex w-fit items-center gap-1 text-xs leading-4 font-normal text-foreground-muted"
              onPointerEnter={() => setIsOverLastUsed(true)}
              onPointerLeave={() => setIsOverLastUsed(false)}
            >
              <HistoryIcon aria-hidden="true" size={16} />
              {last_used_at ? (
                <TimestampTooltip timestamp={last_used_at} side="top">
                  <span>{timeFromNow(last_used_at)}</span>
                </TimestampTooltip>
              ) : (
                <span>{t('common.not_used_yet')}</span>
              )}
            </span>
          </div>

          <div className="absolute top-4 right-4">
            <Actions
              rule={rule}
              rulesPath={rulesPath}
              isSelected={isSelected}
              currentPosition={currentPosition}
              totalRules={totalRules}
              onReorder={reorderRule}
              disabled={!canWrite}
              onOpenChange={setIsMenuOpen}
            />
          </div>
        </Link>
      </InfoTooltip>
    </div>
  );
};
